import React from "react";
import ReportTable from "../ReportTable";
import { principleTitles, renderYesNo, safeText } from "./helpers";
import usePrincipleData from "./usePrincipleData";
import MultiHeaderReportTable from "../MultiHeaderReportTable";
import useReportingYearLabels from "./useReportingYearLabels";

const twoYearRows = (obj, idPrefix) => [
  {
    _id: `${idPrefix}-curr`,
    year: "Current Financial Year",
    value: safeText(obj?.currentFinancialYear, "N/A"),
  },
  {
    _id: `${idPrefix}-prev`,
    year: "Previous Financial Year",
    value: safeText(obj?.previousFinancialYear, "N/A"),
  },
];

const Principle5 = ({ styles }) => {
  const { loading, principle } = usePrincipleData(5);
  const { currentYear, previousYear } = useReportingYearLabels();
  const reportingYearLabels = useReportingYearLabels();
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};

  const fyLineA = currentYear
    ? `a. In FY ${currentYear} (Current Financial Year)`
    : "a. In Current Financial Year (N/A)";
  const fyLineB = previousYear
    ? `b. In FY ${previousYear} (Previous Financial Year)`
    : "b. In Previous Financial Year (N/A)";

  const fyCurrentColLabel = currentYear
    ? `In FY ${currentYear} (Current Financial Year)`
    : "Current Financial Year (N/A)";
  const fyPreviousColLabel = previousYear
    ? `In FY ${previousYear} (Previous Financial Year)`
    : "Previous Financial Year (N/A)";

  const poshCurrentColHdr = currentYear
    ? `FY ${currentYear} (Current Financial Year)`
    : "Current Financial Year (N/A)";
  const poshPreviousColHdr = previousYear
    ? `FY ${previousYear} (Previous Financial Year)`
    : "Previous Financial Year (N/A)";

  const poshRows = [
    {
      _id: "p5-e7-0",
      particular:
        "Total Complaints reported under Sexual Harassment on of Women at Workplace (Prevention, Prohibition and Redressal) Act, 2013 (POSH)",
      current: safeText(
        essential?.complaintsFiledUnderSexualHarassemnt?.totalComplaints
          ?.currentFinancialYear,
        "N/A",
      ),
      previous: safeText(
        essential?.complaintsFiledUnderSexualHarassemnt?.totalComplaints
          ?.previousFinancialYear,
        "N/A",
      ),
    },
    {
      _id: "p5-e7-1",
      particular: "Complaints on POSH as a % of female employees / workers",
      current: safeText(
        essential?.complaintsFiledUnderSexualHarassemnt
          ?.complaintsFemaleEmployee?.currentFinancialYear,
        "N/A",
      ),
      previous: safeText(
        essential?.complaintsFiledUnderSexualHarassemnt
          ?.complaintsFemaleEmployee?.previousFinancialYear,
        "N/A",
      ),
    },
    {
      _id: "p5-e7-2",
      particular: "Complaints on POSH upheld",
      current: safeText(
        essential?.complaintsFiledUnderSexualHarassemnt?.poshUpheld
          ?.currentFinancialYear,
        "N/A",
      ),
      previous: safeText(
        essential?.complaintsFiledUnderSexualHarassemnt?.poshUpheld
          ?.previousFinancialYear,
        "N/A",
      ),
    },
  ];

  const trainingColumns = [
    { field: "category", label: "Category" },
    {
      label: fyCurrentColLabel,
      children: [
        { field: "cTotal", label: "Total(No.)" },
        { field: "cCovered", label: "No. of covered employees" },
        { field: "cPct", label: "%" },
      ],
    },
    {
      label: fyPreviousColLabel,
      children: [
        { field: "pTotal", label: "Total(No.)" },
        { field: "pCovered", label: "No. of covered employees" },
        { field: "pPct", label: "%" },
      ],
    },
  ];

  const mapTrainingRows = (current = {}, previous = {}, rowLabels, idPrefix) =>
    rowLabels.map((label, idx) => {
      const key =
        idx === 0
          ? "permanentEmployees"
          : idx === 1
            ? "otherThanPermanent"
            : "total";
      const c = current?.[key];
      const p = previous?.[key];
      return {
        _id: `${idPrefix}-${idx}`,
        category: label,
        cTotal: safeText(c?.total, "N/A"),
        cCovered: safeText(c?.noOfEmployee, "N/A"),
        cPct: safeText(c?.percentage, "N/A"),
        pTotal: safeText(p?.total, "N/A"),
        pCovered: safeText(p?.noOfEmployee, "N/A"),
        pPct: safeText(p?.percentage, "N/A"),
      };
    });

  const wageCategories = [
    { label: "a. Permanent employees", key: "permanentEmployees" },
    {
      label: "b. Other than permanent employees",
      key: "otherThanPermanentEmployees",
    },
    { label: "c. Permanent workers", key: "permanentWorkers" },
    {
      label: "d. Other than permanent workers",
      key: "otherThanPermanentWorkers",
    },
  ];

  const wageTableColumns = [
    { field: "gender", label: "Category", width: "200px" },
    {
      label: fyCurrentColLabel,
      children: [
        { field: "cTotal", label: "Total" },
        { field: "cEqNum", label: "Equal to minimum wage (Number)" },
        { field: "cEqPct", label: "Equal to minimum wage (Percentage)" },
        { field: "cMoreNum", label: "More than minimum wage (Number)" },
        { field: "cMorePct", label: "More than minimum wage (Percentage)" },
      ],
    },
    {
      label: fyPreviousColLabel,
      children: [
        { field: "pTotal", label: "Total" },
        { field: "pEqNum", label: "Equal to minimum wage (Number)" },
        { field: "pEqPct", label: "Equal to minimum wage (Percentage)" },
        { field: "pMoreNum", label: "More than minimum wage (Number)" },
        { field: "pMorePct", label: "More than minimum wage (Percentage)" },
      ],
    },
  ];

  const mapWageRows = (categoryKey, idPrefix) => {
    const curr =
      essential?.minimumWages?.currentFinancialYear?.[categoryKey] || {};
    const prev =
      essential?.minimumWages?.previousFinancialYear?.[categoryKey] || {};

    const rowFor = (label, key, idx) => ({
      _id: `${idPrefix}-${idx}`,
      gender: label,
      cTotal: safeText(curr?.[key]?.total, "N/A"),
      cEqNum: safeText(curr?.[key]?.equalWageMin?.number, "N/A"),
      cEqPct: safeText(curr?.[key]?.equalWageMin?.percentage, "N/A"),
      cMoreNum: safeText(curr?.[key]?.equalWageMax?.number, "N/A"),
      cMorePct: safeText(curr?.[key]?.equalWageMax?.percentage, "N/A"),
      pTotal: safeText(prev?.[key]?.total, "N/A"),
      pEqNum: safeText(prev?.[key]?.equalWageMin?.number, "N/A"),
      pEqPct: safeText(prev?.[key]?.equalWageMin?.percentage, "N/A"),
      pMoreNum: safeText(prev?.[key]?.equalWageMax?.number, "N/A"),
      pMorePct: safeText(prev?.[key]?.equalWageMax?.percentage, "N/A"),
    });

    return [
      rowFor("Male", "male", 0),
      rowFor("Female", "female", 1),
      rowFor("Total", "totalAddition", 2),
    ];
  };

  const medianRemunerationRows = [
    ["Board of Directors", "boardOfDirectors"],
    ["Key Management Personnel", "keyManagementPersonnel"],
    [
      "Employees other than Board of Directors and Key Management Personnel",
      "otherEmployees",
    ],
    ["Workers", "workers"],
  ].map(([category, key], idx) => ({
    _id: `p5-e3a-${idx}`,
    category,
    numberofMale: safeText(
      essential?.remunerationDetails?.medianRemuneration?.[key]?.numberofMale,
      "N/A",
    ),
    medianRenumerationMale: safeText(
      essential?.remunerationDetails?.medianRemuneration?.[key]
        ?.medianRenumerationMale,
      "N/A",
    ),
    numberofFemale: safeText(
      essential?.remunerationDetails?.medianRemuneration?.[key]?.numberofFemale,
      "N/A",
    ),
    medianRenumerationFemale: safeText(
      essential?.remunerationDetails?.medianRemuneration?.[key]
        ?.medianRenumerationFemale,
      "N/A",
    ),
  }));

  const complaintsMap = [
    ["Sexual Harassment", "sexualHarassemnt1"],
    ["Discrimination at workplace", "discrimination2"],
    ["Child Labour", "childLabor3"],
    ["Forced Labour/Involuntary Labour", "forcedLabor4"],
    ["Wages", "wages5"],
    ["Other human rights related issues", "other6"],
  ];

  const complaintRows = (yearKey, idPrefix) =>
    complaintsMap.map(([type, key], idx) => ({
      _id: `${idPrefix}-${idx}`,
      type,
      filed: safeText(
        essential?.complaintsFiled?.[yearKey]?.[key]?.fliedYear,
        "N/A",
      ),
      pending: safeText(
        essential?.complaintsFiled?.[yearKey]?.[key]?.pending,
        "N/A",
      ),
      remarks: safeText(
        essential?.complaintsFiled?.[yearKey]?.[key]?.remark,
        "N/A",
      ),
    }));

  const assessmentRows = [
    ["Sexual Harassment", "sexualHarassment"],
    ["Discrimination at workplace", "discrimination"],
    ["Child Labor", "childLabor"],
    ["Forced Labor / Involuntary Labor", "forcedLabor"],
    ["Wages", "wages"],
    ["Other", "other"],
  ].map(([category, key], idx) => ({
    _id: `p5-e8-${idx}`,
    category,
    assessed:
      key === "other"
        ? `${safeText(essential?.assessmentsForYear?.other?.others, "N/A")} (${safeText(essential?.assessmentsForYear?.other?.percentageValue, "N/A")}%)`
        : safeText(essential?.assessmentsForYear?.[key]?.plantsOffices, "N/A"),
  }));

  const valueChainRows = [
    ["Sexual Harassment", "sexualHarassment"],
    ["Discrimination at workplace", "discrimination"],
    ["Child Labor", "childLabor"],
    ["Forced Labor / Involuntary Labor", "forcedLabor"],
    ["Wages", "wages"],
    ["Other", "other"],
  ].map(([category, key], idx) => ({
    _id: `p5-l4-${idx}`,
    category,
    assessed:
      key === "other"
        ? `${safeText(leadership?.other?.others, "N/A")} (${safeText(leadership?.other?.percentageValue, "N/A")}%)`
        : safeText(leadership?.[key], "N/A"),
  }));

  return (
    <div className={styles.principleBlock}>
      <div
        className={styles.principleHeading}
      >{`PRINCIPLE 5: ${principleTitles[4]}`}</div>
      {loading ? (
        <div className={styles.meta}>Loading principle 5...</div>
      ) : null}

      <div className={styles.band}>I. ESSENTIAL INDICATOR</div>

      <div className={styles.question}>
        1. Employees and workers who have been provided training on human rights
        issues and policy(ies) of the entity, in the following format:
      </div>

      <div className={styles.answer}>a. Employees</div>
      <MultiHeaderReportTable
        showSerialNumber={false}
        columns={trainingColumns}
        rows={mapTrainingRows(
          essential?.humanRightsTraining?.employee?.currentFinancialYear,
          essential?.humanRightsTraining?.employee?.previousFinancialYear,
          ["Permanent Employees", "Other than Permanent", "Total"],
          "p5-e1-emp",
        )}
      />

      <div className={styles.answer}>b. Workers</div>
      <MultiHeaderReportTable
        showSerialNumber={false}
        columns={trainingColumns}
        rows={mapTrainingRows(
          essential?.humanRightsTraining?.workers?.currentFinancialYear,
          essential?.humanRightsTraining?.workers?.previousFinancialYear,
          ["Permanent Workers", "Other than Permanent", "Total Workers"],
          "p5-e1-work",
        )}
      />

      <div className={styles.question}>
        2. Details of minimum wages paid to employees and workers:
      </div>
      {wageCategories.map((c) => (
        <div key={c.key}>
          <div className={styles.answer}>{c.label}</div>
          <MultiHeaderReportTable
            showSerialNumber={false}
            columns={wageTableColumns}
            rows={mapWageRows(c.key, `p5-e2-${c.key}`)}
          />
        </div>
      ))}

      <div className={styles.question}>
        3. Details of remuneration / salary / wages, in the following format:
      </div>
      <div className={styles.answer}>a. Median remuneration / wages:</div>
      <MultiHeaderReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          {
            label: "Male",
            children: [
              { field: "numberofMale", label: "Total(No.)" },
              {
                field: "medianRenumerationMale",
                label:
                  "Median remuneration/salary/wages of respective category",
              },
            ],
          },
          {
            label: "Female",
            children: [
              { field: "numberofFemale", label: "Total(No.)" },
              {
                field: "medianRenumerationFemale",
                label:
                  "Median remuneration/salary/wages of respective category",
              },
            ],
          },
        ]}
        rows={medianRemunerationRows}
      />

      <div className={styles.answer}>
        b. Gross wages paid to females as % of total wages paid by the entity,
        in the following format:
      </div>
      <MultiHeaderReportTable
        showSerialNumber={false}
        columns={[
          { field: "label", label: "" },
          { field: "current", label: fyCurrentColLabel },
          { field: "previous", label: fyPreviousColLabel },
        ]}
        rows={[
          {
            _id: "p5-e3b-0",
            label: "Gross wages paid to females as % of total wages",
            current: safeText(
              essential?.remunerationDetails?.femalePercentage
                ?.currentFinancialYear,
              "N/A",
            ),
            previous: safeText(
              essential?.remunerationDetails?.femalePercentage
                ?.previousFinancialYear,
              "N/A",
            ),
          },
        ]}
      />

      <div className={styles.question}>
        4. Do you have a focal point (Individual/ Committee) responsible for
        addressing human rights impacts or issues caused or contributed to by
        the business?
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.humanRightsFocalPoint?.status)}
        {String(essential?.humanRightsFocalPoint?.status) === "true"
          ? `, ${safeText(essential?.humanRightsFocalPoint?.remark, "N/A")}`
          : ""}
      </div>

      <div className={styles.question}>
        5. Details of the internal mechanisms in place to redress grievances
        related to human rights issues.
      </div>
      <div className={styles.answer}>
        {safeText(essential?.internalMechanismsHumanRightsGrievances, "N/A")}
      </div>

      <div className={styles.question}>
        6. Number of Complaints on the following made by employees and workers:
      </div>
      <div className={`${styles.answer} ${styles.boldAnswer}`}>{fyLineA}</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "type", label: "Type" },
          { field: "filed", label: "Filed during the year" },
          { field: "pending", label: "Pending resolution at year end" },
          { field: "remarks", label: "Remarks" },
        ]}
        rows={complaintRows("currentFinancialYear1", "p5-e5-current")}
      />
      <div className={`${styles.answer} ${styles.boldAnswer}`}>{fyLineB}</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "type", label: "Type" },
          { field: "filed", label: "Filed during the year" },
          { field: "pending", label: "Pending resolution at year end" },
          { field: "remarks", label: "Remarks" },
        ]}
        rows={complaintRows("previousFinancialYear1", "p5-e5-prev")}
      />

      <div className={styles.question}>
        7. Complaints filed under the Sexual Harassment of Women at Workplace
        (Prevention, Prohibition and Redressal) Act, 2013
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "particular", label: "Particulars", width: "46%" },
          { field: "current", label: poshCurrentColHdr },
          { field: "previous", label: poshPreviousColHdr },
        ]}
        rows={poshRows}
      />

      <div className={styles.question}>
        8. Mechanisms to prevent adverse consequences to the complainant in the
        above mentioned cases.
      </div>
      <div className={styles.answer}>
        {safeText(essential?.mechanismsToPreventAdverseConsequences, "N/A")}
      </div>

      <div className={styles.question}>
        9. Do human rights requirements form part of your business agreements
        and contracts?
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.humanRightsRequirementsInAgreements?.action)}
        {String(essential?.humanRightsRequirementsInAgreements?.action) ===
        "true"
          ? `, ${safeText(essential?.humanRightsRequirementsInAgreements?.bref, "N/A")}`
          : ""}
      </div>

      <div className={styles.question}>10. Assessments for the year:</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          {
            field: "assessed",
            label:
              "% of your plants and offices that were assessed (by entity or statutory authorities or third parties)",
          },
        ]}
        rows={assessmentRows}
      />

      <div className={styles.question}>
        11. Provide details of any corrective actions taken or underway to
        address significant risks / concerns arising from the assessments at
        Question 10 above.
      </div>
      <div className={styles.answer}>
        {safeText(essential?.correctiveActionsForAssessments, "N/A")}
      </div>

      <div className={styles.band}>II. LEADERSHIP INDICATOR</div>

      <div className={styles.question}>
        1. Details of a business process being modified / introduced as a result
        of addressing human rights grievances / complaints.
      </div>
      <div className={styles.answer}>
        {safeText(leadership?.businessProcessBeingModified, "N/A")}
      </div>

      <div className={styles.question}>
        2. Details of the scope and coverage of any Human rights due-diligence
        conducted.
      </div>
      <div className={styles.answer}>
        {safeText(leadership?.humanRightsDueDiligence, "N/A")}
      </div>

      <div className={styles.question}>
        3. Is the premise / office of the entity accessible to differently abled
        visitors, as per the requirements of the Rights of Persons with
        Disabilities Act, 2016?
      </div>
      <div className={styles.answer}>
        {safeText(leadership?.accessibilityToDifferentlyAbled, "N/A")}
      </div>

      <div className={styles.question}>
        4. Details on assessment of value chain partners:
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "" },
          {
            field: "assessed",
            label:
              "% of value chain partners (by value of business done with such partners) that were assessed",
          },
        ]}
        rows={valueChainRows}
      />

      <div className={styles.question}>
        5. Provide details of any corrective actions taken or underway to
        address significant risks / concerns arising from the assessments at
        Question 4 above.
      </div>
      <div className={styles.answer}>
        {safeText(leadership?.correctiveActionTakenUnderway, "N/A")}
      </div>

      {/* <div className={styles.question}>
        6. Describe the internal mechanisms in place to redress grievances related to human rights issues.
      </div>
      <div className={styles.answer}>{safeText(leadership?.grievanceRedressalMechanisms, "N/A")}</div> */}

      <div className={styles.pageBreak} />
    </div>
  );
};

export default Principle5;
